import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';

import Strings from '../../../../Strings';
import { DATE_FORMAT_MONTH_NAME, DATE_TIME_SPACE_12_UPPERCASE, PROGRAM_STATUS_SORT_ORDER, RPM_PATIENT_STATUS } from '../../../../constants';
import { actions } from '../redux/actions';
import { GET_ENROLLMENT_STATUS_HISTORY_RESULT } from '../redux/constants';
import { ORG_FEATURES, orgFeatureEnabled } from '../../../../utils/userPermissions';
import { PageHeader } from '../../../../components/PageHeader';
import Select from '../../../../components/Select';
import { getProgramDisplayName } from '../../../../utils/cmsPrograms';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import { OUTREACH_STATUS_ENUM } from '../../../../utils/rpmPatientFields';
import { toolTipForTable } from '../../../../utils';

function EnrollmentHistory({ patientId, getEnrollmentStatusHistory, enrollmentId }) {
  const [isLoading, setIsLoading] = useState(false);
  const [historyByEnrollment, setHistoryByEnrollment] = useState({});
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(enrollmentId || '');
  const [dateSortType, setDateSortType] = useState('descending');
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);

  const getSortedEnrollmentIds = React.useCallback((data) => {
    return Object.keys(data || {}).sort((a, b) => {
      const aOrder = PROGRAM_STATUS_SORT_ORDER[data[a].status] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = PROGRAM_STATUS_SORT_ORDER[data[b].status] ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) return aOrder - bOrder;

      if (data[a].status === RPM_PATIENT_STATUS.ended && data[b].status === RPM_PATIENT_STATUS.ended) {
        const aEnd = data[a].history.find(h => h.new_status === RPM_PATIENT_STATUS.ended)?.changed_at || 0;
        const bEnd = data[b].history.find(h => h.new_status === RPM_PATIENT_STATUS.ended)?.changed_at || 0;
        if (aEnd !== bEnd) return new Date(bEnd).getTime() - new Date(aEnd).getTime();
      }

      const aInit = data?.[a]?.init_date ? new Date(data[a].init_date).getTime() : 0;
      const bInit = data?.[b]?.init_date ? new Date(data[b].init_date).getTime() : 0;
      if (aInit !== bInit) return aInit - bInit;
      return a.localeCompare(b);
    });
  }, []);

  useEffect(() => {
    if (!patientId) {
      return;
    }

    setIsLoading(true);
    getEnrollmentStatusHistory(dateSortType).then(resp => {
      if (resp.type === GET_ENROLLMENT_STATUS_HISTORY_RESULT) {
        const apiData = resp.response || {};
        setHistoryByEnrollment(apiData);

        const enrollmentIds = getSortedEnrollmentIds(apiData);
        const initialEnrollmentId = enrollmentId || enrollmentIds[0] || '';
        setSelectedEnrollmentId(initialEnrollmentId);
      } else {
        setHistoryByEnrollment({});
        setSelectedEnrollmentId('');
      }
      setIsLoading(false);
    });
  }, [patientId, enrollmentId, dateSortType, getSortedEnrollmentIds]);

  const enrollmentOptions = useMemo(() => {
    const historyIds = getSortedEnrollmentIds(historyByEnrollment);
    return historyIds.map(id => {
      const enrollment = historyByEnrollment[id];
      const programName = getProgramDisplayName(enrollment.program, enrollment.subprogram) || Strings.capPatient.noProgram;
      let statusDescription = enrollment.status ? (Strings.capPatient.patientStatus[enrollment.status] || enrollment.status) : '';
      if (enrollment.status === RPM_PATIENT_STATUS.ended) {
        const endedEntry = enrollment.history.find(h => h.new_status === RPM_PATIENT_STATUS.ended);
        if (endedEntry?.changed_at) {
          statusDescription += ` on ${moment(endedEntry.changed_at).format(DATE_FORMAT_MONTH_NAME)}`;
        }
      }
      return {
        value: id,
        label: `${programName} (${statusDescription})`,
      };
    });
    
  }, [historyByEnrollment, getSortedEnrollmentIds]);

  const selectedHistory = selectedEnrollmentId ? historyByEnrollment[selectedEnrollmentId]?.history || [] : [];
  const formatTransition = (oldValue, newValue) => {
    if (!oldValue && !newValue) {
      return '';
    }
    if (oldValue && newValue && oldValue === newValue) {
      return 'No change';
    }
    const oldDisplay = Strings.capPatient.patientStatus[oldValue] || OUTREACH_STATUS_ENUM[oldValue]?.label || oldValue;
    const newDisplay = Strings.capPatient.patientStatus[newValue] || OUTREACH_STATUS_ENUM[newValue]?.label || newValue;
    return `${oldDisplay || 'Not set'} \u2192 ${newDisplay || 'Not set'}`;
  };


  const onSortingChange = (sortingState) => {
    if (sortingState.length === 0) {
      setDateSortType(undefined);
    }
    const dateSort = sortingState.find(s => s.id === 'changed_at');
    if (dateSort) {
      setDateSortType(dateSort.desc ? 'descending' : 'ascending');
    } else {
      setDateSortType(undefined);
    }
  };

  const columns = [
    columnHelper.accessor('changed_at', {
      header: Strings.date,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
    }),
    columnHelper.accessor('status_transition', {
      header: Strings.statusTransition || "Status Transition",
      accessorFn: row => formatTransition(row.old_status, row.new_status),
      enableSorting: false,
    })
  ];
  if (extendedOutreachStatusEnabled) {
    columns.push(
    columnHelper.accessor('outreach_transition', {
      header: Strings.outreachTransition || "Outreach Transition",
      accessorFn: row => formatTransition(row.old_outreach_status, row.new_outreach_status),
      enableSorting: false,
    }));
  }
  columns.push(
    columnHelper.accessor('kit_transition', {
      header: Strings.kitTransition || 'Kit Transition',
      accessorFn: row => formatTransition(row.old_kit_id, row.new_kit_id),
      enableSorting: false,
    }),
    columnHelper.accessor('reason', {
      header: Strings.reason,
      enableSorting: false,
      cell: info => toolTipForTable(info.getValue()) || '-',
    }),
    columnHelper.accessor('changed_by', {
      header: Strings.changedBy || "Changed By",
      enableSorting: false,
    })
  );

  return (
    <>
      <PageHeader
        left={Strings.widgets.enrollmentHistory}
        right={enrollmentId ? null : (
          <Select
            id="enrollment_id"
            value={selectedEnrollmentId}
            onChange={option => setSelectedEnrollmentId(option.value)}
            data={enrollmentOptions}
            className="enrollment-select"
          />
        )}
        isBlack={false}
      />
      <Table3
        name="enrollment-history"
        columns={columns}
        data={selectedHistory}
        isLoading={isLoading}
        onSortingChange={onSortingChange}
      />
    </>
  );
}

EnrollmentHistory.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  getEnrollmentStatusHistory: PropTypes.func.isRequired,
  enrollmentId: PropTypes.string,
};


const mapDispatchToProps = (dispatch, props) => ({
  getEnrollmentStatusHistory: sortType => dispatch(actions.getEnrollmentStatusHistory(props.patientId, sortType)),
});

EnrollmentHistory.widgetId = 'EnrollmentHistoryWidget';

export default connect(null, mapDispatchToProps)(EnrollmentHistory);
